<template>
  <!--
    Состав комбо в детализации заказа.

    Позиция-комбо хранится одной строкой «Комбо», а выбранные слоты лежат рядом
    только как ID (`comboSelections`). Названия приезжают с бэкенда в `comboGroups`,
    и без них гость видел в истории безымянную строку и не мог понять, что заказывал.
  -->
  <ul v-if="groups.length > 0" class="combo-composition" data-testid="order-combo-composition">
    <li v-for="group in groups" :key="group.groupId" class="combo-composition__group">
      <span class="combo-composition__group-name">{{ group.groupName }}:</span>
      <span class="combo-composition__products">
        <span
          v-for="(product, idx) in group.products"
          :key="`${group.groupId}-${product.id}`"
          class="combo-composition__product"
        >
          {{ product.name }}<span
            v-if="modifiersLabel(product)"
            class="combo-composition__modifiers"
          >&nbsp;{{ modifiersLabel(product) }}</span><template
            v-if="idx < group.products.length - 1"
          >, </template>
        </span>
      </span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { OrderComboGroup, OrderComboProduct } from '@entities/order'

interface Props {
  item: any
}

const props = defineProps<Props>()

/** Слоты комбо; у обычных товаров и у старых заказов без резолва — пусто */
const groups = computed<OrderComboGroup[]>(() => {
  const raw = props.item?.comboGroups
  return Array.isArray(raw) ? raw : []
})

/**
 * Добавки и отбавки слота одной строкой.
 * Это выбор гостя в конструкторе комбо («Изменить состав»), без него состав
 * в истории не совпадает с тем, что приехало на кухню.
 */
function modifiersLabel(product: OrderComboProduct): string {
  const parts: string[] = []

  const addons = (product.addons ?? []).map(addon => addon.name).filter(Boolean)
  if (addons.length > 0) parts.push(`+ ${addons.join(', ')}`)

  const excludes = (product.excludes ?? []).map(exclude => exclude.name).filter(Boolean)
  if (excludes.length > 0) parts.push(`без ${excludes.join(', ')}`)

  return parts.length > 0 ? `(${parts.join('; ')})` : ''
}
</script>

<style scoped lang="scss">
.combo-composition {
  @apply list-none p-0 m-0 grid gap-0.5;

  &__group {
    @apply flex flex-wrap gap-x-1 text-xs text-hui-default-500;
  }

  &__group-name {
    @apply shrink-0 text-hui-default-400;
  }

  &__products {
    @apply min-w-0 text-hui-default-600;
  }

  &__modifiers {
    @apply text-hui-default-400;
  }
}
</style>
